import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { QuestionnaireResponse } from "@medplum/fhirtypes";
import { Loading, useMedplum } from "@medplum/react";

import { Group, Paper, Stack, Title } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import { WordsDiff } from "@/components/WordsDiff";
import { extractNestedAnswers } from "@/encounters/components/PostpartumEncounter/utils/postpartum";
import {
  PostpartumEncounterQuestionnaireURLs,
  PostpartumSocialHealthQuestionsDisplayNames,
  PostpartumSocialHealthQuestionsLinkIds,
} from "@/encounters/constants/postpartum-encounter";
import { fetchAmendmentHistoryByQuestionnaireUrl } from "@/encounters/services/questionnaire-response";

const SocialHealthDiff = ({ currentVersionIndex }: { currentVersionIndex: number }) => {
  const { encounterId } = useParams<{ encounterId: string }>();
  const medplum = useMedplum();

  const [isFetchingInitialData, setIsFetchingInitialData] = useState(true);
  const [currentVersion, setCurrentVersion] = useState<QuestionnaireResponse>();
  const [previousVersion, setPreviousVersion] = useState<QuestionnaireResponse>();

  useEffect(() => {
    const getAmendmentHistory = async () => {
      medplum.invalidateSearches("QuestionnaireResponse");
      try {
        const [foundAmendmentHistory] = await fetchAmendmentHistoryByQuestionnaireUrl(
          medplum,
          PostpartumEncounterQuestionnaireURLs.SocialHealth,
          encounterId!,
        );

        if (!foundAmendmentHistory) return;

        setCurrentVersion(foundAmendmentHistory[currentVersionIndex]);
        setPreviousVersion(foundAmendmentHistory[currentVersionIndex + 1]);
      } finally {
        setIsFetchingInitialData(false);
      }
    };
    getAmendmentHistory();
  }, [currentVersionIndex, encounterId, medplum]);

  if (isFetchingInitialData) return <Loading />;

  // If no social health data has ever been recorded
  if (!currentVersion) {
    return (
      <Paper p="md" withBorder>
        <Stack mb="lg">
          <Title order={2}>Social Health</Title>
        </Stack>
        <TextWrapper py="lg" c="gray.6" ta="center">
          No social health data has been recorded for this encounter.
        </TextWrapper>
      </Paper>
    );
  }

  // If social health data exists but no previous version to compare against,
  // create an empty previous version to reuse the existing diff logic
  const effectivePreviousVersion =
    previousVersion || ({ item: [] } as unknown as QuestionnaireResponse);

  // Extract values for all social health fields using extractNestedAnswers
  const previousAnswers = extractNestedAnswers(effectivePreviousVersion);
  const currentAnswers = extractNestedAnswers(currentVersion);

  const previousSmoke =
    previousAnswers[PostpartumSocialHealthQuestionsLinkIds.PostpartumSocialHealthSmoke]?.join(
      ", ",
    ) || "";
  const currentSmoke =
    currentAnswers[PostpartumSocialHealthQuestionsLinkIds.PostpartumSocialHealthSmoke]?.join(
      ", ",
    ) || "";

  const previousSmokeAmount =
    previousAnswers[PostpartumSocialHealthQuestionsLinkIds.PostpartumSocialHealthSmokeAmount]?.join(
      ", ",
    ) || "";
  const currentSmokeAmount =
    currentAnswers[PostpartumSocialHealthQuestionsLinkIds.PostpartumSocialHealthSmokeAmount]?.join(
      ", ",
    ) || "";

  const previousSmokeFrequency =
    previousAnswers[
      PostpartumSocialHealthQuestionsLinkIds.PostpartumSocialHealthSmokeFrequency
    ]?.join(", ") || "";
  const currentSmokeFrequency =
    currentAnswers[
      PostpartumSocialHealthQuestionsLinkIds.PostpartumSocialHealthSmokeFrequency
    ]?.join(", ") || "";

  const previousDrink =
    previousAnswers[PostpartumSocialHealthQuestionsLinkIds.PostpartumSocialHealthDrink]?.join(
      ", ",
    ) || "";
  const currentDrink =
    currentAnswers[PostpartumSocialHealthQuestionsLinkIds.PostpartumSocialHealthDrink]?.join(
      ", ",
    ) || "";

  const previousDrinkAmount =
    previousAnswers[PostpartumSocialHealthQuestionsLinkIds.PostpartumSocialHealthDrinkAmount]?.join(
      ", ",
    ) || "";
  const currentDrinkAmount =
    currentAnswers[PostpartumSocialHealthQuestionsLinkIds.PostpartumSocialHealthDrinkAmount]?.join(
      ", ",
    ) || "";

  const previousDrinkFrequency =
    previousAnswers[
      PostpartumSocialHealthQuestionsLinkIds.PostpartumSocialHealthDrinkFrequency
    ]?.join(", ") || "";
  const currentDrinkFrequency =
    currentAnswers[
      PostpartumSocialHealthQuestionsLinkIds.PostpartumSocialHealthDrinkFrequency
    ]?.join(", ") || "";

  const previousDrugs =
    previousAnswers[PostpartumSocialHealthQuestionsLinkIds.PostpartumSocialHealthDrugs]?.join(
      ", ",
    ) || "";
  const currentDrugs =
    currentAnswers[PostpartumSocialHealthQuestionsLinkIds.PostpartumSocialHealthDrugs]?.join(
      ", ",
    ) || "";

  const previousDrugsType =
    previousAnswers[PostpartumSocialHealthQuestionsLinkIds.PostpartumSocialHealthDrugsType]?.join(
      ", ",
    ) || "";
  const currentDrugsType =
    currentAnswers[PostpartumSocialHealthQuestionsLinkIds.PostpartumSocialHealthDrugsType]?.join(
      ", ",
    ) || "";

  const previousWork =
    previousAnswers[PostpartumSocialHealthQuestionsLinkIds.PostpartumSocialHealthWork]?.join(
      ", ",
    ) || "";
  const currentWork =
    currentAnswers[PostpartumSocialHealthQuestionsLinkIds.PostpartumSocialHealthWork]?.join(", ") ||
    "";

  const previousWorkCapacity =
    previousAnswers[
      PostpartumSocialHealthQuestionsLinkIds.PostpartumSocialHealthWorkCapacity
    ]?.join(", ") || "";
  const currentWorkCapacity =
    currentAnswers[PostpartumSocialHealthQuestionsLinkIds.PostpartumSocialHealthWorkCapacity]?.join(
      ", ",
    ) || "";

  const previousWorkFrequency =
    previousAnswers[
      PostpartumSocialHealthQuestionsLinkIds.PostpartumSocialHealthWorkFrequency
    ]?.join(", ") || "";
  const currentWorkFrequency =
    currentAnswers[
      PostpartumSocialHealthQuestionsLinkIds.PostpartumSocialHealthWorkFrequency
    ]?.join(", ") || "";

  const previousSupport =
    previousAnswers[PostpartumSocialHealthQuestionsLinkIds.PostpartumSocialHealthSupport]?.join(
      ", ",
    ) || "";
  const currentSupport =
    currentAnswers[PostpartumSocialHealthQuestionsLinkIds.PostpartumSocialHealthSupport]?.join(
      ", ",
    ) || "";

  const previousSupportBy =
    previousAnswers[PostpartumSocialHealthQuestionsLinkIds.PostpartumSocialHealthSupportBy]?.join(
      ", ",
    ) || "";
  const currentSupportBy =
    currentAnswers[PostpartumSocialHealthQuestionsLinkIds.PostpartumSocialHealthSupportBy]?.join(
      ", ",
    ) || "";

  const previousNotes =
    previousAnswers[PostpartumSocialHealthQuestionsLinkIds.PostpartumSocialHealthNotes]?.join(
      ", ",
    ) || "";
  const currentNotes =
    currentAnswers[PostpartumSocialHealthQuestionsLinkIds.PostpartumSocialHealthNotes]?.join(
      ", ",
    ) || "";

  return (
    <Paper p="md" withBorder>
      <Stack mb="lg">
        <Title order={2}>Social Health</Title>
      </Stack>
      <Group grow align="stretch">
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Previous
            </Title>
            <Stack gap="md">
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSocialHealthQuestionsDisplayNames.PostpartumSocialHealthSmoke}
                </TextWrapper>
                <WordsDiff showRemoved current={currentSmoke} previous={previousSmoke} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSocialHealthQuestionsDisplayNames.PostpartumSocialHealthAmount}
                </TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentSmokeAmount}
                  previous={previousSmokeAmount}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSocialHealthQuestionsDisplayNames.PostpartumSocialHealthSmokeFrequency}
                </TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentSmokeFrequency}
                  previous={previousSmokeFrequency}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSocialHealthQuestionsDisplayNames.PostpartumSocialHealthDrink}
                </TextWrapper>
                <WordsDiff showRemoved current={currentDrink} previous={previousDrink} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSocialHealthQuestionsDisplayNames.PostpartumSocialHealthAmount}
                </TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentDrinkAmount}
                  previous={previousDrinkAmount}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSocialHealthQuestionsDisplayNames.PostpartumSocialHealthDrinkFrequency}
                </TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentDrinkFrequency}
                  previous={previousDrinkFrequency}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSocialHealthQuestionsDisplayNames.PostpartumSocialHealthDrugs}
                </TextWrapper>
                <WordsDiff showRemoved current={currentDrugs} previous={previousDrugs} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSocialHealthQuestionsDisplayNames.PostpartumSocialHealthDrugsType}
                </TextWrapper>
                <WordsDiff showRemoved current={currentDrugsType} previous={previousDrugsType} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSocialHealthQuestionsDisplayNames.PostpartumSocialHealthWork}
                </TextWrapper>
                <WordsDiff showRemoved current={currentWork} previous={previousWork} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSocialHealthQuestionsDisplayNames.PostpartumSocialHealthWorkCapacity}
                </TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentWorkCapacity}
                  previous={previousWorkCapacity}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSocialHealthQuestionsDisplayNames.PostpartumSocialHealthWorkFrequency}
                </TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentWorkFrequency}
                  previous={previousWorkFrequency}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSocialHealthQuestionsDisplayNames.PostpartumSocialHealthSupport}
                </TextWrapper>
                <WordsDiff showRemoved current={currentSupport} previous={previousSupport} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSocialHealthQuestionsDisplayNames.PostpartumSocialHealthSupportBy}
                </TextWrapper>
                <WordsDiff showRemoved current={currentSupportBy} previous={previousSupportBy} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSocialHealthQuestionsDisplayNames.PostpartumSocialHealthNotes}
                </TextWrapper>
                <WordsDiff showRemoved current={currentNotes} previous={previousNotes} />
              </Stack>
            </Stack>
          </Stack>
        </Paper>
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Amended
            </Title>
            <Stack gap="md">
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSocialHealthQuestionsDisplayNames.PostpartumSocialHealthSmoke}
                </TextWrapper>
                <WordsDiff current={currentSmoke} previous={previousSmoke} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSocialHealthQuestionsDisplayNames.PostpartumSocialHealthAmount}
                </TextWrapper>
                <WordsDiff current={currentSmokeAmount} previous={previousSmokeAmount} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSocialHealthQuestionsDisplayNames.PostpartumSocialHealthSmokeFrequency}
                </TextWrapper>
                <WordsDiff current={currentSmokeFrequency} previous={previousSmokeFrequency} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSocialHealthQuestionsDisplayNames.PostpartumSocialHealthDrink}
                </TextWrapper>
                <WordsDiff current={currentDrink} previous={previousDrink} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSocialHealthQuestionsDisplayNames.PostpartumSocialHealthAmount}
                </TextWrapper>
                <WordsDiff current={currentDrinkAmount} previous={previousDrinkAmount} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSocialHealthQuestionsDisplayNames.PostpartumSocialHealthDrinkFrequency}
                </TextWrapper>
                <WordsDiff current={currentDrinkFrequency} previous={previousDrinkFrequency} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSocialHealthQuestionsDisplayNames.PostpartumSocialHealthDrugs}
                </TextWrapper>
                <WordsDiff current={currentDrugs} previous={previousDrugs} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSocialHealthQuestionsDisplayNames.PostpartumSocialHealthDrugsType}
                </TextWrapper>
                <WordsDiff current={currentDrugsType} previous={previousDrugsType} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSocialHealthQuestionsDisplayNames.PostpartumSocialHealthWork}
                </TextWrapper>
                <WordsDiff current={currentWork} previous={previousWork} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSocialHealthQuestionsDisplayNames.PostpartumSocialHealthWorkCapacity}
                </TextWrapper>
                <WordsDiff current={currentWorkCapacity} previous={previousWorkCapacity} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSocialHealthQuestionsDisplayNames.PostpartumSocialHealthWorkFrequency}
                </TextWrapper>
                <WordsDiff current={currentWorkFrequency} previous={previousWorkFrequency} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSocialHealthQuestionsDisplayNames.PostpartumSocialHealthSupport}
                </TextWrapper>
                <WordsDiff current={currentSupport} previous={previousSupport} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSocialHealthQuestionsDisplayNames.PostpartumSocialHealthSupportBy}
                </TextWrapper>
                <WordsDiff current={currentSupportBy} previous={previousSupportBy} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumSocialHealthQuestionsDisplayNames.PostpartumSocialHealthNotes}
                </TextWrapper>
                <WordsDiff current={currentNotes} previous={previousNotes} />
              </Stack>
            </Stack>
          </Stack>
        </Paper>
      </Group>
    </Paper>
  );
};

export default SocialHealthDiff;
